// Password reset form component for Cognito
// Requirements: 8.5

import React, { useState } from 'react';
import { AlertTriangleIcon } from '../common/icons';
import { CognitoAuthService } from '../../services/cognitoAuthService';
import './PasswordResetForm.css';

interface PasswordResetFormProps {
  onResetSuccess: () => void;
  onBackToLogin: () => void;
}

export const PasswordResetForm: React.FC<PasswordResetFormProps> = ({
  onResetSuccess,
  onBackToLogin
}) => {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting || !email.trim()) return;

    const emailValidation = CognitoAuthService.validateEmail(email);
    if (!emailValidation.isValid) {
      setError(emailValidation.error);
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      await CognitoAuthService.forgotPassword(email.trim());
      setSuccessMessage('Password reset code sent! Please check your email.');
      setStep('code');
    } catch (err: any) {
      console.error('Password reset request error:', err);
      setError(err.message || 'Failed to send password reset code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting || !verificationCode.trim() || !newPassword || !confirmPassword) return;

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordValidation = CognitoAuthService.validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors[0]);
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      await CognitoAuthService.forgotPasswordSubmit(email, verificationCode.trim(), newPassword);
      setSuccessMessage('Password reset successfully! You can now sign in with your new password.');
      
      // Wait a moment to show success message, then redirect
      setTimeout(() => {
        onResetSuccess();
      }, 2000);
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(err.message || 'Password reset failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      await CognitoAuthService.forgotPassword(email);
      setSuccessMessage('New password reset code sent! Please check your email.');
    } catch (err: any) {
      console.error('Resend code error:', err);
      setError(err.message || 'Failed to resend password reset code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    switch (field) {
      case 'email':
        setEmail(value);
        break;
      case 'verificationCode':
        // Only allow digits and limit to 6 characters
        setVerificationCode(value.replace(/\D/g, '').slice(0, 6));
        break;
      case 'newPassword':
        setNewPassword(value);
        break;
      case 'confirmPassword':
        setConfirmPassword(value);
        break;
    }
    
    if (error) {
      setError('');
    }
  };

  if (step === 'email') {
    return (
      <div className="password-reset-form">
        <div className="password-reset-form__header">
          <h2>Reset Password</h2>
          <p>Enter your email address and we'll send you a code to reset your password.</p>
        </div>

        <form onSubmit={handleEmailSubmit} className="password-reset-form__form">
          {error && (
            <div className="password-reset-form__error" role="alert">
              <AlertTriangleIcon width={16} height={16} className="password-reset-form__error-icon" />
              {error}
            </div>
          )}

          {successMessage && (
            <div className="password-reset-form__success" role="alert">
              {successMessage}
            </div>
          )}

          <div className="password-reset-form__field">
            <label htmlFor="email" className="password-reset-form__label">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="password-reset-form__input"
              placeholder="Enter your email address"
              disabled={isSubmitting}
              autoComplete="email"
              required
            />
          </div>

          <button
            type="submit"
            className={`password-reset-form__submit ${
              !email.trim() || isSubmitting ? 'password-reset-form__submit--disabled' : ''
            }`}
            disabled={!email.trim() || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="password-reset-form__spinner"></span>
                Sending Code...
              </>
            ) : (
              'Send Reset Code'
            )}
          </button>

          <div className="password-reset-form__actions">
            <button
              type="button"
              className="password-reset-form__link"
              onClick={onBackToLogin}
              disabled={isSubmitting}
            >
              Back to sign in
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="password-reset-form">
      <div className="password-reset-form__header">
        <h2>Enter New Password</h2>
        <p>
          Enter the verification code sent to <strong>{email}</strong> and your new password.
        </p>
      </div>

      <form onSubmit={handlePasswordResetSubmit} className="password-reset-form__form">
        {error && (
          <div className="password-reset-form__error" role="alert">
            <AlertTriangleIcon width={16} height={16} className="password-reset-form__error-icon" />
            {error}
          </div>
        )}

        {successMessage && (
          <div className="password-reset-form__success" role="alert">
            {successMessage}
          </div>
        )}

        <div className="password-reset-form__field">
          <label htmlFor="verificationCode" className="password-reset-form__label">
            Verification Code
          </label>
          <input
            id="verificationCode"
            type="text"
            value={verificationCode}
            onChange={(e) => handleInputChange('verificationCode', e.target.value)}
            className="password-reset-form__input password-reset-form__input--code"
            placeholder="000000"
            disabled={isSubmitting}
            maxLength={6}
            autoComplete="one-time-code"
            required
          />
        </div>

        <div className="password-reset-form__field">
          <label htmlFor="newPassword" className="password-reset-form__label">
            New Password
          </label>
          <div className="password-reset-form__password-wrapper">
            <input
              id="newPassword"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => handleInputChange('newPassword', e.target.value)}
              className="password-reset-form__input"
              placeholder="Enter your new password"
              disabled={isSubmitting}
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              className="password-reset-form__password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isSubmitting}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
        </div>

        <div className="password-reset-form__field">
          <label htmlFor="confirmPassword" className="password-reset-form__label">
            Confirm New Password
          </label>
          <input
            id="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            className="password-reset-form__input"
            placeholder="Confirm your new password"
            disabled={isSubmitting}
            autoComplete="new-password"
            required
          />
        </div>

        <button
          type="submit"
          className={`password-reset-form__submit ${
            !verificationCode.trim() || !newPassword || !confirmPassword || isSubmitting 
              ? 'password-reset-form__submit--disabled' : ''
          }`}
          disabled={!verificationCode.trim() || !newPassword || !confirmPassword || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="password-reset-form__spinner"></span>
              Resetting Password...
            </>
          ) : (
            'Reset Password'
          )}
        </button>

        <div className="password-reset-form__actions">
          <button
            type="button"
            className="password-reset-form__link"
            onClick={handleResendCode}
            disabled={isSubmitting}
          >
            Resend verification code
          </button>

          <button
            type="button"
            className="password-reset-form__link"
            onClick={onBackToLogin}
            disabled={isSubmitting}
          >
            Back to sign in
          </button>
        </div>
      </form>
    </div>
  );
};

export default PasswordResetForm;